import React from 'react';
import Image from 'next/future/image';
import Head from 'next/head';
import Link from 'next/link';
import { Blog } from '@lambda/commons/apis/shopify/types/storefront';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { useRouter } from 'next/router';
import settings from '@/settings';
import Sidebar from '@/components/blogs/sidebar';
import shopify from '@/lib/shopify';
import i18n from '@/settings/i18n';
import { getShortenCountryName } from '@/lib/use-origin-country';
import { WebsiteSchema } from '@/components/homepage/WebsiteSchema';
import { logger } from '@/lib/logger';

type Props = { blog: Blog };

const t = i18n.init();

export const getStaticProps = async () => {
  const response = await shopify.fetch({
    query: `query ($query: String!) {
              blog(handle: $query){
                title
                articles(first:12 reverse:true, sortKey:PUBLISHED_AT){
                  edges{
                    node{
                      title
                      handle
                      content
                      contentHtml
                      excerpt
                      tags 
                      id
                      publishedAt
                      image { url }
                      onlineStoreUrl
                    }
                  }
                }
              }
            }`,
    variables: { query: 'News' },
    skipLogging: true,
  });
  const { blog } = response;

  if (blog == null || blog.articles.edges.length === 0) {
    logger.info('Blog not found or empty');

    return { notFound: true };
  }

  return { props: { blog }, revalidate: 10 };
};

export default function Blogs(props: Props) {
  const router = useRouter();

  dayjs.extend(advancedFormat);
  const { blog } = props;
  const [firstArticle] = blog.articles.edges.map((e) => e.node);
  const tagged = router?.query?.tagged?.toString();
  const allArticles = blog.articles.edges.map((e) => e.node);

  return (
    <div>
      <Head>
        <title>News - Reebelo {getShortenCountryName()}</title>
        <WebsiteSchema />
        <meta
          name="description"
          content={t`Reebelo ${{
            country: settings.country,
          }} is APAC's leading marketplace for sustainable tech with offices in ${{
            offices: settings.country_office_locations,
          }}. The positive customer experience at Reebelo ${{
            country: settings.country,
          }} is of utmost importance to us. Please check here for Reebelo ${{
            country: settings.country,
          }} News.`}
        />
      </Head>
      <div className="reebelo-container pt-5 pb-6 md:pt-8 md:pb-20">
        <h1 className="mb-5 text-left text-base font-semibold !leading-5 text-gray-700 xxs:text-lg xxs:!leading-6 xs:text-xl sm:!leading-normal md:mb-7 md:text-2xl xl:text-3xl xl:!leading-9">
          News
        </h1>
        <div className="flex flex-wrap gap-5 md:gap-8 lg:flex-nowrap">
          <div className="w-full lg:w-[60%] xl:w-[70%]">
            {!tagged && (
              <div className="mb-7 w-full md:mb-12">
                <Link href={`/blogs/${firstArticle.handle}`} passHref>
                  <a>
                    <div className="flex w-full overflow-hidden">
                      <Image
                        src={firstArticle.image.url}
                        alt="Banner"
                        className="w-full object-cover duration-1000 ease-in-out hover:scale-[1.07]"
                        height={535}
                        width={990}
                      />
                    </div>

                    <h2 className="my-3 text-lg font-bold text-gray-700 duration-500 ease-in-out hover:text-blue-500 sm:text-2xl md:text-2xl">
                      {firstArticle.title}
                    </h2>
                  </a>
                </Link>
                <p className="mb-4 text-sm text-gray-700 md:text-base">
                  {dayjs(firstArticle.publishedAt).format('MMM Do YYYY')}
                </p>
                <p className="text-sm text-gray-700 md:text-base">
                  {firstArticle.excerpt
                    ? firstArticle.excerpt
                    : `${firstArticle?.content.slice(0, 200)}...`}
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 gap-7 md:grid-cols-2 md:gap-y-14">
              {allArticles
                .filter((e) =>
                  // Filter first item if tagged is false.
                  tagged ? e : e.handle !== firstArticle.handle,
                )
                .filter((e) =>
                  // Filter items based on tagged if tagged is true.
                  tagged ? e.tags.toString().toLowerCase().includes(tagged) : e,
                )
                .map((article) => (
                  <div className="w-full" key={article.handle}>
                    <Link href={`/blogs/${article.handle}`} passHref>
                      <a>
                        {article?.image?.url && (
                          <Image
                            src={article.image.url}
                            alt="blog4"
                            className="w-full object-cover duration-1000 ease-in-out hover:scale-[1.07]"
                            height={260}
                            width={480}
                          />
                        )}
                        <h2 className="my-3 text-lg font-bold text-gray-700 duration-500 ease-in-out hover:text-blue-500 sm:text-xl lg:text-[22px]">
                          {article.title}
                        </h2>
                      </a>
                    </Link>
                    <p className="mb-4 text-sm text-gray-700 md:text-base">
                      {article.tags.length > 0 && (
                        <>
                          <span>{article.tags[0]}</span>
                          <span className="mx-2.5 inline-block h-1.5 w-1.5 rounded-full bg-gray-700"></span>
                        </>
                      )}
                      <span>
                        {dayjs(article.publishedAt).format('MMM Do YYYY')}
                      </span>
                    </p>
                    <p className="text-sm text-gray-700 md:text-base">
                      {article.excerpt ||
                        `${article?.content.slice(0, 200)}...`}
                    </p>
                  </div>
                ))}
            </div>
          </div>
          <div className="hidden w-full lg:block lg:w-[40%] xl:w-[30%]">
            <Sidebar articles={blog.articles} />
          </div>
        </div>
      </div>
    </div>
  );
}
